'use client';
import { sendGTMEvent } from '@next/third-parties/google';
// Google Ads Conversion Tracking for Lead Generation

export interface ConversionEvent {
  send_to: string;
  value?: number;
  currency?: string;
  transaction_id?: string;
}

// Track Google Ads conversion
export function trackConversion(conversionLabel: string, value: number = 0) {
  if (typeof window !== 'undefined' && window.gtag) {
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId) return;

    window.gtag('event', 'conversion', {
      send_to: `${measurementId}/${conversionLabel}`,
      value: value,
      currency: 'USD',
    });
  }
}

// Track lead form submission (primary conversion)
export function trackLeadFormSubmission({
  name,
  email,
  phone,
  puppyType,
  leadSource,
}: {
  name: string;
  email: string;
  phone?: string;
  puppyType?: string;
  leadSource: string;
}) {
  sendGTMEvent({
    event: 'lead_form_submission',
    lead_source: leadSource,
    puppy_interest: puppyType || 'not_specified',
    user_data: {
      email,
      phone_number: phone || '',
    },
  });
}

// Track phone click (call conversion)
export function trackPhoneClick() {
  if (typeof window !== 'undefined' && window.gtag) {
    trackConversion('phone_call_click', 30);

    window.gtag('event', 'phone_call_initiated', {
      event_category: 'Lead Generation',
      event_label: 'Phone Number Click',
      value: 30,
    });
  }
}

// Track email click
export function trackEmailClick() {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'email_click', {
      event_category: 'Lead Generation',
      event_label: 'Email Address Click',
      value: 20,
    });
  }
}

// Track CTA button clicks
export function trackCTAClick(ctaLocation: string, ctaText: string) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'cta_click', {
      event_category: 'Engagement',
      event_label: `${ctaLocation} - ${ctaText}`,
      cta_location: ctaLocation,
      cta_text: ctaText,
    });
  }
}

// Track scroll depth (engagement metric)
export function trackScrollDepth(percentage: number) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'scroll_depth', {
      event_category: 'Engagement',
      event_label: `${percentage}% Scrolled`,
      value: percentage,
    });
  }
}

// Track video play (if testimonial videos)
export function trackVideoPlay(videoTitle: string) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'video_start', {
      event_category: 'Engagement',
      event_label: videoTitle,
      video_title: videoTitle,
    });
  }
}

// Track puppy card click
export function trackPuppyCardClick(puppyName: string, puppyVariety: string) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'puppy_card_click', {
      event_category: 'Product Interest',
      event_label: `${puppyName} - ${puppyVariety}`,
      puppy_name: puppyName,
      puppy_variety: puppyVariety,
    });
  }
}

// Track exit intent popup view
export function trackExitIntentPopup(action: 'shown' | 'closed' | 'converted') {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'exit_intent_popup', {
      event_category: 'Lead Generation',
      event_label: action,
      popup_action: action,
    });
  }
}

// Track sticky bar interaction
export function trackStickyBarClick() {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'sticky_bar_click', {
      event_category: 'Lead Generation',
      event_label: 'Sticky CTA Bar',
    });
  }
}

// Track page engagement time
export function trackEngagementTime(seconds: number) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'user_engagement', {
      event_category: 'Engagement',
      engagement_time_msec: seconds * 1000,
    });
  }
}
